import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, SlidersHorizontal, X, Copy, Bookmark, ChevronDown, Send } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const DOMAIN_TAGS = ['Creative', 'Technical', 'Business', 'Legal', 'Science', 'Philosophy', 'Education', 'Healthcare', 'Finance', 'Engineering'];
const SORT_OPTIONS = [
  { value: 'popular', label: 'Popular' },
  { value: 'newest', label: 'Newest' },
  { value: 'az', label: 'A-Z' },
];

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Voices() {
  const { user } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [sortBy, setSortBy] = useState('popular');
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('directory');
  const [myCreated, setMyCreated] = useState([]);
  const [myLibrary, setMyLibrary] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [submitting, setSubmitting] = useState({});

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    API.get('/api/personas')
      .then(r => setPersonas(r.data.personas || r.data || []))
      .catch(() => setPersonas([]))
      .finally(() => setLoading(false));
    API.get('/api/persona-groups')
      .then(r => setGroups(r.data.groups || []))
      .catch(() => setGroups([]));
  }, []);

  const loadMyLibrary = useCallback(async () => {
    if (!user) return;
    setMyLoading(true);
    try {
      const res = await API.get('/api/user/personas');
      setMyCreated(res.data.created || []);
      setMyLibrary(res.data.library || []);
    } catch (err) { console.error('Load my library error:', err); }
    setMyLoading(false);
  }, [user]);

  useEffect(() => {
    if (activeTab === 'my-library' && user) loadMyLibrary();
  }, [activeTab, user, loadMyLibrary]);

  const handleSubmitToDirectory = async (slug) => {
    setSubmitting(prev => ({ ...prev, [slug]: 'loading' }));
    try {
      await API.post(`/api/personas/${slug}/submit`);
      setSubmitting(prev => ({ ...prev, [slug]: 'submitted' }));
      setMyCreated(prev => prev.map(p => p.slug === slug ? { ...p, visibility: 'pending_review' } : p));
    } catch (err) {
      setSubmitting(prev => ({ ...prev, [slug]: 'error' }));
      console.error('Submit error:', err);
    }
  };

  const toggleTag = useCallback((tag) => {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }, []);

  const filtered = useMemo(() => {
    let result = [...personas];

    // Search filter
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.tagline?.toLowerCase().includes(q)
      );
    }

    // Tag filter
    if (activeTags.length > 0) {
      result = result.filter(p =>
        p.domain_tags?.some(t => activeTags.includes(t))
      );
    }

    // Group filter
    if (activeGroup) {
      const group = groups.find(g => g.slug === activeGroup);
      if (group?.members) {
        const memberIds = new Set(group.members.map(m => m.id));
        result = result.filter(p => memberIds.has(p.id));
      }
    }

    // Sort
    if (sortBy === 'popular') {
      result.sort((a, b) => (b.use_count || 0) - (a.use_count || 0));
    } else if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sortBy === 'az') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    return result;
  }, [personas, debouncedSearch, activeTags, activeGroup, groups, sortBy]);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-10">
          <motion.h1
            className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] mb-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Voice Library
          </motion.h1>
          <motion.p
            className="text-[#A0A0A0] text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            Discover AI personas for your debates
          </motion.p>
        </div>

        {/* Tab switcher */}
        {user && (
          <div className="flex gap-1 mb-6 border-b border-[#2A2A2A]">
            {[{ key: 'directory', label: 'Directory' }, { key: 'my-library', label: 'My Library' }].map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-xs font-mono transition-colors border-b-2 -mb-px ${
                  activeTab === t.key
                    ? 'border-[#FF4D00] text-[#FF4D00]'
                    : 'border-transparent text-[#A0A0A0] hover:text-[#F5F5F5]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* My Library Tab */}
        {activeTab === 'my-library' && user ? (
          <div>
            {myLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 bg-[#1A1A1A] border border-[#2A2A2A] animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {/* Created Personas */}
                {myCreated.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xs font-mono text-[#FF4D00] mb-4">CREATED BY YOU</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myCreated.map((persona, i) => (
                        <motion.div
                          key={persona.id || persona.slug || i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03, duration: 0.2 }}
                          className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/30 transition-colors"
                        >
                          <div className="flex items-center gap-3 mb-2 cursor-pointer" onClick={() => setSelectedPersona(persona)}>
                            <span className="text-3xl">{persona.avatar_emoji || '🎭'}</span>
                            <div className="flex-1">
                              <h3 className="text-sm font-bold font-['Barlow_Condensed']">{persona.name}</h3>
                              <p className="text-[10px] text-[#A0A0A0] font-mono">{persona.use_count || 0} uses</p>
                            </div>
                            {/* Visibility badge */}
                            <span className={`text-[8px] px-1.5 py-0.5 border font-mono ${
                              persona.visibility === 'pending_review' ? 'text-[#F59E0B] border-[#F59E0B]/30' :
                              persona.visibility === 'public' || persona.visibility === 'platform' ? 'text-[#22C55E] border-[#22C55E]/30' :
                              'text-[#A0A0A0] border-[#2A2A2A]'
                            }`}>
                              {(persona.visibility || 'private').toUpperCase()}
                            </span>
                          </div>
                          <p className="text-xs text-[#A0A0A0] mb-3 line-clamp-2 cursor-pointer" onClick={() => setSelectedPersona(persona)}>
                            {persona.tagline || persona.description}
                          </p>
                          {/* Submit to Directory button — only for private personas */}
                          {persona.visibility === 'private' && (
                            <button
                              onClick={() => handleSubmitToDirectory(persona.slug)}
                              disabled={submitting[persona.slug] === 'loading'}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono border border-[#FF4D00]/30 text-[#FF4D00] hover:bg-[#FF4D00]/10 transition-colors disabled:opacity-50"
                            >
                              <Send className="w-3 h-3" />
                              {submitting[persona.slug] === 'loading' ? 'Submitting...' : 'Submit to Directory'}
                            </button>
                          )}
                          {(persona.visibility === 'pending_review' || submitting[persona.slug] === 'submitted') && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono text-[#F59E0B] border border-[#F59E0B]/30 bg-[#F59E0B]/5">
                              Submitted — Pending Review
                            </span>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Saved/Forked Personas */}
                {myLibrary.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xs font-mono text-[#FF4D00] mb-4">SAVED TO LIBRARY</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {myLibrary.map((persona, i) => (
                        <motion.div
                          key={persona.id || persona.slug || i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03, duration: 0.2 }}
                          onClick={() => setSelectedPersona(persona)}
                          className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/30 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-3xl">{persona.avatar_emoji || '🎭'}</span>
                            <div>
                              <h3 className="text-sm font-bold font-['Barlow_Condensed']">{persona.name}</h3>
                              <p className="text-[10px] text-[#A0A0A0] font-mono">{persona.use_count || 0} uses</p>
                            </div>
                          </div>
                          <p className="text-xs text-[#A0A0A0] mb-2 line-clamp-2">{persona.tagline || persona.description}</p>
                          <div className="flex gap-1 flex-wrap">
                            {persona.domain_tags?.map(t => (
                              <span key={t} className="px-1.5 py-0.5 text-[8px] font-mono border border-[#2A2A2A] text-[#A0A0A0]">{t}</span>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
                {myCreated.length === 0 && myLibrary.length === 0 && (
                  <div className="text-center py-16 border border-[#2A2A2A]">
                    <Bookmark className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
                    <p className="text-[#A0A0A0] text-sm">Your library is empty. Browse the directory and save voices!</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
        <>
        {/* Search + Sort row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] transition-colors"
              placeholder="Search voices by name or description..."
              data-testid="voice-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors min-w-[140px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>{SORT_OPTIONS.find(o => o.value === sortBy)?.label}</span>
              <ChevronDown className="w-3.5 h-3.5 ml-auto" />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] z-10 min-w-[140px]">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-xs hover:bg-[#2A2A2A] transition-colors ${
                      sortBy === opt.value ? 'text-[#FF4D00]' : 'text-[#A0A0A0]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Group filter chips */}
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="group-filters">
            {groups.map(g => (
              <button
                key={g.slug}
                onClick={() => setActiveGroup(activeGroup === g.slug ? null : g.slug)}
                className={`px-3 py-1 min-h-[44px] sm:min-h-0 text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                  activeGroup === g.slug
                    ? 'border-[#A855F7] text-[#A855F7] bg-[#A855F7]/10'
                    : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#A855F7]/30'
                }`}
                data-testid={`group-${g.slug}`}
              >
                {g.icon && <span>{g.icon}</span>}
                {g.name}
                <span className="text-[9px] opacity-60">{g.member_count || g.members?.length || 0}</span>
              </button>
            ))}
            {activeGroup && (
              <button
                onClick={() => setActiveGroup(null)}
                className="px-3 py-1 text-xs font-mono text-[#EF4444] hover:text-[#FF6B6B] transition-colors"
              >
                Clear group
              </button>
            )}
          </div>
        )}

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          {DOMAIN_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`px-3 py-1 min-h-[44px] sm:min-h-0 text-xs font-mono border transition-colors ${
                activeTags.includes(tag)
                  ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10'
                  : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/30'
              }`}
              data-testid={`tag-${tag.toLowerCase()}`}
            >
              {tag}
            </button>
          ))}
          {activeTags.length > 0 && (
            <button
              onClick={() => setActiveTags([])}
              className="px-3 py-1 text-xs font-mono text-[#EF4444] hover:text-[#FF6B6B] transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Results count */}
        {(debouncedSearch || activeTags.length > 0 || activeGroup) && (
          <p className="text-[10px] font-mono text-[#FF4D00] mb-4">
            {filtered.length} voice{filtered.length !== 1 ? 's' : ''} found
          </p>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-32 bg-[#1A1A1A] border border-[#2A2A2A] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-[#2A2A2A]">
            <Search className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
            <p className="text-[#A0A0A0] text-sm">No voices match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((persona, i) => (
              <motion.div
                key={persona.id || persona.slug || i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03, duration: 0.2 }}
                onClick={() => setSelectedPersona(persona)}
                className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/30 cursor-pointer transition-colors"
                data-testid={`voice-card-${persona.id || i}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{persona.avatar_emoji || '🎭'}</span>
                  <div>
                    <h3 className="text-sm font-bold font-['Barlow_Condensed']">{persona.name}</h3>
                    <p className="text-[10px] text-[#A0A0A0] font-mono">{persona.use_count || 0} uses</p>
                  </div>
                </div>
                <p className="text-xs text-[#A0A0A0] mb-2 line-clamp-2">{persona.tagline || persona.description}</p>
                <div className="flex gap-1 flex-wrap">
                  {persona.domain_tags?.map(t => (
                    <span key={t} className="px-1.5 py-0.5 text-[8px] font-mono border border-[#2A2A2A] text-[#A0A0A0]">{t}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedPersona && (
          <PersonaModal
            persona={selectedPersona}
            onClose={() => setSelectedPersona(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PersonaModal({ persona, onClose }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Content */}
      <motion.div
        className="relative w-full max-w-lg bg-[#1A1A1A] border border-[#2A2A2A] p-4 md:p-6 max-h-[85vh] overflow-y-auto"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <span className="text-5xl">{persona.avatar_emoji || '🎭'}</span>
          <div>
            <h2 className="text-xl font-bold font-['Barlow_Condensed']">{persona.name}</h2>
            <p className="text-xs text-[#A0A0A0] font-mono">{persona.use_count || 0} uses</p>
          </div>
        </div>

        {/* Tagline */}
        {persona.tagline && (
          <p className="text-sm text-[#F5F5F5] mb-4">{persona.tagline}</p>
        )}

        {/* Domain tags */}
        {persona.domain_tags?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-5">
            {persona.domain_tags.map(t => (
              <span key={t} className="px-2 py-0.5 text-[9px] font-mono border border-[#2A2A2A] text-[#A0A0A0]">{t}</span>
            ))}
          </div>
        )}

        {/* Personality / Description */}
        {(persona.personality || persona.description) && (
          <div className="mb-5">
            <span className="text-[10px] font-mono text-[#FF4D00] block mb-2">PERSONALITY</span>
            <p className="text-xs text-[#A0A0A0] leading-relaxed">{persona.personality || persona.description}</p>
          </div>
        )}

        {/* System prompt preview */}
        {persona.system_prompt && (
          <div className="mb-5">
            <span className="text-[10px] font-mono text-[#FF4D00] block mb-2">SYSTEM PROMPT PREVIEW</span>
            <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] max-h-40 overflow-y-auto">
              <p className="text-[11px] text-[#A0A0A0] font-mono whitespace-pre-wrap leading-relaxed">
                {persona.system_prompt.length > 500
                  ? persona.system_prompt.slice(0, 500) + '...'
                  : persona.system_prompt}
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium hover:bg-[#FF4D00]/90 transition-colors">
            <Bookmark className="w-4 h-4" />
            Save to Library
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] border border-[#2A2A2A] text-sm text-[#F5F5F5] hover:border-[#FF4D00]/30 transition-colors">
            <Copy className="w-4 h-4" />
            Fork & Customize
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
